import { useEffect, useState } from 'react'
import { TOOLTIP } from '../../../../../../../constants/tooltips'
import { InputWidth } from '../../../../../../../types/aoraki/input-width-types'
import { WellYieldDrawdownFields } from '../../../../../../../types/form-types'
import NotesWrapperView from '../../../../../../components/well-forms/notes/notes-wrapper-view'
import WellViewButtons from '../../../../../../components/wells/well-view-buttons'
import { useGetWellData } from '../../../../../../hooks/use-api'
import { getRandomID } from '../../../../../../services/form-utils'
import { useParamsOr404 } from '../../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  Card,
  FormField,
  FormFieldRow,
  Heading,
  ReadOnlyField,
  RecordContent,
  RecordFooter,
  useTitleChange,
} from '@msaf/core-react'

export default function DrillingTestView() {
  useTitleChange('View Well | Drilling Test')

  const { id, testId } = useParamsOr404('id', 'testId')
  const [fields, setFields] = useState<Partial<WellYieldDrawdownFields>>({})
  const navigate = useNavigate()
  const url = `wells/${id}/drilling-tests/${testId}/view`
  const backUrl = `/wells/${id}/view/drilling-tests`
  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data?.state) {
      setFields(data.state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.state])

  return (
    <>
      <RecordContent>
        <ButtonsContainer containerStyle='left'>
          <Button
            onClick={() => navigate(backUrl)}
            label='Back'
            iconAlignment='left'
            icon='arrow-left'
            buttonStyle='text-action'
            isSkeleton={isLoading}
          />
        </ButtonsContainer>
        <Heading isSkeleton={isLoading} level={2}>
          Yield drawdown test details
        </Heading>

        <FormField isSkeleton={isLoading} labelId='testDate' label='Test date'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='testDate'
            value={`${fields?.testDate ?? ''}`}
          />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='pumpingRateUnit' label='Pumping rate unit'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='pumpingRateUnit'
            value={`${fields?.pumpingRateUnit ?? ''}`}
          />
        </FormField>

        {(Array.isArray(fields.testSteps) ? fields.testSteps : []).map((test, index) => {
          return (
            <Card key={getRandomID()}>
              <Heading level={2}>{`Step ${index + 1}`}</Heading>
              <FormFieldRow>
                <FormField isSkeleton={isLoading} labelId={`stepNo${index}`} label='Step'>
                  <ReadOnlyField
                    isSkeleton={isLoading}
                    labelledBy={`stepNo${index}`}
                    // Use index to display stepNo to user rather than DB value as deletions may not sequence steps correctly.
                    value={`${test.stepNo ?? index + 1}`}
                  />
                </FormField>

                <FormField isSkeleton={isLoading} labelId={`stepDuration${index}`} label='Minutes'>
                  <ReadOnlyField
                    isSkeleton={isLoading}
                    labelledBy={`stepDuration${index}`}
                    value={`${test.stepDuration ?? ''}`}
                  />
                </FormField>
              </FormFieldRow>
              <FormFieldRow>
                <FormField
                  toolTipMessage={TOOLTIP.yield}
                  isSkeleton={isLoading}
                  labelId={`yield${index}`}
                  label='Yield'
                >
                  <ReadOnlyField
                    isSkeleton={isLoading}
                    labelledBy={`yield${index}`}
                    value={`${test.yieldValue ?? ''}`}
                  />
                </FormField>
                <FormField
                  toolTipMessage={TOOLTIP.drawdown}
                  isSkeleton={isLoading}
                  labelId={`drawDown${index}`}
                  label='Drawdown (m)'
                >
                  <ReadOnlyField
                    isSkeleton={isLoading}
                    labelledBy={`drawDown${index}`}
                    value={`${test.drawdown ?? ''}`}
                  />
                </FormField>
              </FormFieldRow>
            </Card>
          )
        })}
      </RecordContent>
      <RecordFooter
        notes={
          Array.isArray(fields.notes) &&
          !!fields.notes.length && <NotesWrapperView notes={fields.notes} isLoading={isLoading} />
        }
        areActionsFixed
      >
        <WellViewButtons wellId={id} />
      </RecordFooter>
    </>
  )
}
